import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CreditCard, Calendar, DollarSign, User, Edit2, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface Invoice {
  id: string;
  amount_cents: number;
  status: string;
  paid_at: string | null;
  created_at: string;
}

interface SubscriptionProfile {
  full_name: string | null;
  cpf: string | null;
  phone: string | null;
  health_area: string | null;
}

interface Subscription {
  id: string;
  status: string;
  plan_code: string;
  price_cents: number;
  started_at: string;
  next_billing_date: string | null;
  profiles: SubscriptionProfile | null;
}

interface AssinaturaDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  onEditPlan: () => void;
  onViewCards: () => void;
  onCancel: () => void;
}

export function AssinaturaDetailModal({
  open,
  onOpenChange,
  subscriptionId,
  onEditPlan,
  onViewCards,
  onCancel
}: AssinaturaDetailModalProps) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    if (open && subscriptionId) {
      loadDetails();
    }
  }, [open, subscriptionId]);

  const loadDetails = async () => {
    try {
      setLoading(true);

      // Load subscription with profile
      const { data: subData, error: subError } = await supabase
        .from("subscriptions")
        .select(`
          *,
          profiles:user_id (
            full_name,
            cpf,
            phone,
            health_area
          )
        `)
        .eq("id", subscriptionId)
        .single();

      if (subError) throw subError;
      setSubscription(subData);

      // Load invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from("invoices")
        .select("*")
        .eq("subscription_id", subscriptionId)
        .order("created_at", { ascending: false })
        .limit(5);

      if (invoicesError) throw invoicesError;
      setInvoices(invoicesData || []);

    } catch (error) {
      console.error("Error loading subscription details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { label: "Ativa", variant: "default" as const },
      canceled: { label: "Cancelada", variant: "destructive" as const },
      past_due: { label: "Pendente", variant: "destructive" as const },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || {
      label: status,
      variant: "secondary" as const
    };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes da Assinatura</DialogTitle>
          <DialogDescription>
            Visualize e gerencie informações completas da assinatura
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : subscription ? (
          <div className="space-y-6">
            {/* Professional Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Profissional</h3>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <p className="font-medium">{subscription.profiles?.full_name}</p>
                <p className="text-sm text-muted-foreground">CPF: {subscription.profiles?.cpf}</p>
                <p className="text-sm text-muted-foreground">Área: {subscription.profiles?.health_area}</p>
              </div>
            </div>

            <Separator />

            {/* Subscription Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Informações da Assinatura</h3>
              </div>
              <div className="grid gap-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {getStatusBadge(subscription.status)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Plano</span>
                  <span className="font-medium">{subscription.plan_code.toUpperCase()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Valor</span>
                  <span className="font-medium">{formatPrice(subscription.price_cents)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Início</span>
                  <span className="text-sm">{format(new Date(subscription.started_at), "dd/MM/yyyy", { locale: ptBR })}</span>
                </div>
                {subscription.next_billing_date && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Próximo Pagamento</span>
                    <span className="text-sm">{format(new Date(subscription.next_billing_date), "dd/MM/yyyy", { locale: ptBR })}</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Recent Invoices */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Últimos Pagamentos</h3>
              </div>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                      <div>
                        <p className="text-sm font-medium">{formatPrice(invoice.amount_cents)}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(invoice.created_at), "dd/MM/yyyy", { locale: ptBR })}
                        </p>
                      </div>
                      <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                        {invoice.status === 'paid' ? 'Pago' : 'Pendente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum pagamento registrado</p>
              )}
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <Button onClick={onEditPlan} variant="outline" className="w-full">
                <Edit2 className="h-4 w-4 mr-2" />
                Modificar Plano
              </Button>
              <Button onClick={onViewCards} variant="outline" className="w-full">
                <CreditCard className="h-4 w-4 mr-2" />
                Visualizar Cartões
              </Button>
              {subscription.status === 'active' && (
                <Button onClick={onCancel} variant="destructive" className="w-full">
                  <X className="h-4 w-4 mr-2" />
                  Cancelar Assinatura
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            Assinatura não encontrada
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
